

import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Getter, Action, State } from "vuex-class"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import vSelect from "vue-select"

import SkeletonLoader from "@/components/SkeletonLoader.vue"
import DataTable from "../DataTable.vue"
import FieldPermissions from "../Permissions/FieldPermissions.vue"
import CalculatedField from "./CalculatedField.vue"
import TextDisplayWithCopy from "./TextDisplayWithCopy.vue"
import { TemplateDetails, Field, DataDefinition, CalculationType, Team, FieldType } from "@/types"
import { CurrentUser } from "@/types/user"
import api from "@/services/api"
import { DataDef } from "vue/types/options"
import cloneDeep from "lodash/cloneDeep"

import StringValidation from "@/scripts/stringValidation"
import { TenantFeatures } from "@/types/tenantFeatures"
import UserImage from "@/components/UserImage.vue"
import { formatDateMixin } from "@/mixins/formatDateMixin"
import FolderService from "@/services/folder-service"

import { Folder, FolderItems, FolderType } from "@/types/folder"
import ConfigurationColumn from "@/types/configurationColumn"

const setup = namespace("setup")

@Component({
	components: {
		"v-select": vSelect,
		"scrollbar": VuePerfectScrollbar,
		"data-table": DataTable,
		"field-permissions": FieldPermissions,
		"skeleton": SkeletonLoader,
		"user-image": UserImage,
		"text-display-copy": TextDisplayWithCopy
	}
})

export default class FieldSetup extends Vue {
	@Getter("getMe")
	public me!: CurrentUser;

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	@State tenantEnabledFeatures!: { [key: string]: boolean }

	@setup.Getter("getFieldTypes")
	fieldTypes!: FieldType[];

	@setup.Getter("getDataSources")
	dataSources!: any[];

	@setup.Getter("getDefinitions")
	definitions!: DataDefinition[];

	@setup.Action loadFields!: () => Promise<any[]>;

	@setup.State
	fields!: Field[];

	@Getter("getCaseName") getCaseName!: string;
	@Getter("getRecordName") getRecordName!: string;

	teams: Team [] = []

	private folderService = new FolderService()

	folderType = FolderType.Field

	tenantFeatures = TenantFeatures

	public editField: Field | null = null;
	public showSettings: boolean = false;

	private isLoadingOnMount: boolean = true;
	private isLoadingMisc: boolean = false;

	public get hasSettingsAuthorization () : boolean {
		return this.me && !!this.me.isAdmin
	}

	formatDateMixin = formatDateMixin

	public get inheritedDefinitions () {
		if (!this.editField || !this.editField.dataSourceId) {
			return this.definitions
		}

		const dataSource = this.dataSources.find((d: any) => d.id === this.editField!.dataSourceId)
		const dataDefinitions: DataDefinition[] = this.definitions.filter(d => d.id === dataSource.definitionId)

		if (!dataDefinitions || dataDefinitions.length === 0) {
			return this.definitions
		}

		const addDefinitions = (d: DataDefinition) => {
			if (d) {
				const children = this.definitions.filter(d2 => d2.inheritedFrom === d.id)

				children.forEach(childDefinition => {
					if (!dataDefinitions.find(added => added.id === childDefinition.id)) {
						dataDefinitions.push(childDefinition)
						addDefinitions(childDefinition)
					}
				})
			}
		}

		addDefinitions(dataDefinitions[0])

		return dataDefinitions
	}

	private get isLoading () {
		return this.isLoadingOnMount || this.isLoadingMisc
	}

	public async mounted () {
		await this.load()
		this.isLoadingOnMount = false
	}

	public get columns (): ConfigurationColumn[] {
		return [
			{
				key: "folderOrExpandable",
				label: "Folder",
				sortable: false,
				inModal: () => false,
				inTable: this.getIsFeatureEnabled(this.tenantFeatures.FieldFolders),
				hasTemplate: true,
				filterByFormatted: true
			},
			{
				key: "id",
				label: "ID",
				sortable: false,
				readonly: true,
				inTable: this.shouldDisplayIdColumn,
				inModal: (item: Field) => this.shouldDisplayIdColumn && !!item.id,
				dynamicComponent: TextDisplayWithCopy,
				componentProperties: {
					displayValue: true
				},
				hasTemplate: true
			},
			{
				key: "title",
				label: "Title",
				sortable: true,
				errors: (item: any) => StringValidation.validateTitle(item.title)
			},
			{
				key: "description",
				label: "Description",
				dataType: "text-area",
				hasTemplate: true
			},
			{
				key: "folder",
				label: "Folder",
				dataType: "list",
				options: this.folderService.currentUser ? this.folderService.getPermittedSelectionFolders() : [],
				sortable: true,
				inTable: false,
				inModal: () => this.getIsFeatureEnabled(this.tenantFeatures.FieldFolders)
			},
			{
				key: "dataTypeTitle",
				label: "Data Type",
				sortable: true,
				dataType: "list",
				valueColumn: "dataType",
				options: this.enabledFieldTypes,
				errors: (item: any) => item.dataType ? null : "Please select a data type",
				readonly: !!(this.editField && this.editField.id),
				tooltip: "Please select from the available data types.  The data type will enforce the type of information you need to capture such as a time, data object, link, checkbox etc."
			},
			{
				key: "dataSourceTitle",
				label: "Data Source",
				dataType: "list",
				valueColumn: "dataSourceId",
				options: this.dataSources,
				sortable: true,
				inModal: (item: any) => item.dataType === "list" || item.dataType === "list-multiple" || item.dataType === "list-data",
				errors: (item: any) => item.dataType === "list" && !item.dataSourceId ? "Please select a data source" : null
			},
			{
				key: "definitionTitle",
				label: "Data Definition",
				dataType: "list",
				valueColumn: "definitionId",
				options: this.inheritedDefinitions,
				sortable: true,
				inModal: (item: any) => item.dataType === "list-data",
				errors: (item: any) => item.dataType === "list-data" && !item.definitionId && !item.dataSourceId ? "Please select a data source" : null
			},
			{
				key: "extensions",
				inModal: (item: any) => true,
				dynamicComponent: CalculatedField,
				inTable: false,
				errors: (item: any) => {
					if (!item || !item.extensions || item.dataType !== "calculated") {
						return null
					}

					if (item.extensions.calculationType === null ||
						item.extensions.calculationType === undefined) {
						return "Select a calculation type"
					}

					switch (item.extensions.calculationType) {
						case CalculationType.DaysUntilOrFromToday:
						case CalculationType.AddToDate:
							return item.extensions.dateField ? null : "Please enter a date field"
						case CalculationType.Total:
						case CalculationType.Average:
							return item.extensions.dataListField
								? item.extensions.dataItemField ? null : "Please enter a data item field"
								: "Please enter a data field"
						case CalculationType.DaysBetweenDates:
							return item.extensions.startField
								? item.extensions.endField ? null : "Please enter an end field"
								: "Please enter a start field"
						case CalculationType.GreaterThan:
						case CalculationType.LessThan:
							return item.extensions.checkField ? null : "Please enter a field"
						case CalculationType.TimeSpentAs:
							return item.extensions.trackingFieldId ? null : "Please enter a field to track"
						default:
							return null
					}
				},
				componentProperties: {
					tenantEnabledFeatures: this.tenantEnabledFeatures
				}
			},
			{
				key: "allowFiles",
				label: "Allow Files",
				sortable: true,
				hasTemplate: true,
				dataType: "checkbox",
				inModal: (item: any) => !["user-role", "field-metrics"].includes(item.dataType ?? ""),
				tooltip: "Allow you to upload a file with the field."
			},
			{
				key: "displayReadOnly",
				label: "Display as Read Only",
				inTable: false,
				dataType: "checkbox",
				inModal: (item: any) => !["case-number", "calculated", "field-metrics"].includes(item.dataType ?? ""),
				tooltip: "Field displays as read-only"
			},
			{
				key: "created",
				label: "Created",
				sortable: true,
				hasTemplate: true,
				inTable: this.getIsFeatureEnabled(TenantFeatures.FieldColumnShowCreatedUpdatedColumns),
				inModal: () => false
			},
			{
				key: "lastUpdated",
				label: "Last Updated",
				sortable: true,
				hasTemplate: true,
				inTable: this.getIsFeatureEnabled(TenantFeatures.FieldColumnShowCreatedUpdatedColumns),
				inModal: () => false
			},
			{
				key: "permissions.record",
				label: this.getRecordName + " Permissions",
				hasTemplate: true,
				dataType: "permissions",
				inTable: !this.getIsFeatureEnabled(TenantFeatures.FieldColumnHidePermissions)
			},
			{
				key: "permissions.case",
				label: this.getCaseName + " Permissions",
				hasTemplate: true,
				dataType: "permissions",
				inTable: !this.getIsFeatureEnabled(TenantFeatures.FieldColumnHidePermissions)
			}
		]
	}

	private get shouldDisplayIdColumn () {
		return this.getIsFeatureEnabled(TenantFeatures.AdminShowFieldId) && !!this.me.isAdmin
	}

	private async load () {
		await this.loadFields()
		await this.loadTeams()
		await this.folderService.load(this.me, FolderType.Field)
		this.folderService.loadFolderItemGroups(this.fields)
	}

	public get tableRows () : (FolderItems | Field)[] {
		return this.getIsFeatureEnabled(TenantFeatures.FieldFolders) ? this.folderService.tableRows() : this.fields
	}

	async saveField (field: Field) {
		if (field.dataType === "calculated") {
			CalculatedField.stripUnusedOptions(field, this.getIsFeatureEnabled)
		}

		await api.fieldSave(field)
		await this.load()
	}

	async deleteField (field: Field) {
		await api.fieldDelete(field)
		await this.load()
	}

	createNewItem () {
		return this.editField = {
			title: "",
			description: null,
			dataType: null,
			dataSourceId: null,
			dataSourceTitle: null,
			definitionId: null,
			definitionTitle: null,
			allowFiles: false,
			folder: null,
			permissions: {
				record: { view: [], edit: [] },
				case: { view: [], edit: [] }
			}
		} as Field
	}

	async setEditItem (editItem: Field) {
		this.editField = editItem
		await this.folderService.loadFolders()
		return cloneDeep(editItem)
	}

	async deleteDetails (field: Field) {
		if (!field!.id) return
		return api.fieldUsage(field)
	}

	private folderDeleted (folder: Folder) {
		const folderField = this.fields.filter(field => field.folder === folder.title || !this.folderService.isParentFolder(folder.title, field.folder))
		folderField.forEach(field => field.folder = null)
		this.folderService.loadFolderItemGroups(this.fields)
	}

	private async loadTeams () {
		const teams: Team[] = await api.teamsGet()
		this.teams = teams
	}

	private toggleExpandFolder (folder: any) {
		if (!folder.isFolderType) {
			return
		}
		const folderRow: FolderItems = folder
		folderRow.expanded = !folderRow.expanded
	}

	private rowClass (item: any, type: any): string {
		if (!item || type !== "row") return ""
		if (item.isFolderType) return "folder-row"

		return ""
	}

	private get enabledFieldTypes (): FieldType[] {
		return this.fieldTypes?.filter((fieldType) => !fieldType.requiredFeature || this.getIsFeatureEnabled(fieldType.requiredFeature)) ?? []
	}
}
